/**
 * Lead-Connector Event Tracker
 *
 * Wraps @varsitytutors/lead-tracking's lead-connector helpers to provide
 * a unified tracking surface for nrdy-marketing-engine. All events flow
 * through /api/track-event → Supabase Edge Function.
 *
 * This replaces the previous bespoke implementation with the shared library.
 */

import {
  getOrCreateVisitorId,
  sendLeadConnectorEvent,
  trackLCFormAbandon,
  trackLCFormFieldInvalid,
  trackLCFormInteraction,
  trackLCFormStart,
  trackLCFormStep,
  trackLCLeadCreation,
  trackLCPageView,
} from '@varsitytutors/lead-tracking';
import type { LeadConnectorConfig } from '@varsitytutors/lead-tracking';

const LC_CONFIG: LeadConnectorConfig = {
  applicationId: 'nrdy-marketing-engine',
  origin: 'nrdy-marketing-engine',
  trackEventPath: '/api/track-event',
};

// ---------------------------------------------------------------------------
// Re-export for direct use by the tracking pipeline (track.ts)
// ---------------------------------------------------------------------------

/**
 * Send a raw lead-connector event. Used by track() to forward every event
 * to the lead-connector pipeline in parallel with ActionTracker/NewRelic.
 */
export function sendWebAnalyticsEvent(eventName: string, metadata?: Record<string, unknown>): void {
  sendLeadConnectorEvent(LC_CONFIG, eventName, {
    event_data: metadata,
  });
}

// ---------------------------------------------------------------------------
// Convenience wrappers — used by the WebAnalytics component & form hooks
// ---------------------------------------------------------------------------

export function trackPageLoad(): void {
  trackLCPageView(LC_CONFIG);
}

export function trackFormStart(): void {
  trackLCFormStart(LC_CONFIG);
}

export function trackFormStep(panelId: string): void {
  trackLCFormStep(LC_CONFIG, panelId);
}

export function trackFormInteraction(panelId: string, target: string, text?: string): void {
  trackLCFormInteraction(LC_CONFIG, panelId, target, text);
}

export function trackFormFieldInvalid(panelId: string, invalidLabel: string): void {
  trackLCFormFieldInvalid(LC_CONFIG, panelId, invalidLabel);
}

export function trackFormAbandon(panelId?: string): void {
  trackLCFormAbandon(LC_CONFIG, panelId);
}

export function trackLeadCreation(leadformVersion?: string): void {
  trackLCLeadCreation(LC_CONFIG, leadformVersion);
}

export function ensureVisitorId(): string {
  return getOrCreateVisitorId();
}
